<template>
  <div>
    <Head title="Devoluciones" />

    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800">Devoluciones</h1>
        <p class="text-sm text-gray-500">Historial de reintegros, cambios de producto y fondos aplicados a ventas</p>
      </div>
      <div>
        <Link class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold text-sm shadow flex items-center space-x-2 transition-colors" href="/devolutions/create">
          <i class="fa fa-plus"></i>
          <span>Nueva Devolución</span>
        </Link>
      </div>
    </div>

    <!-- FILTROS Y BÚSQUEDA -->
    <div class="bg-white rounded-lg shadow p-4 mb-6 border border-gray-100 flex flex-col md:flex-row gap-4 justify-between items-center">
      <div class="w-full md:w-1/2 relative">
        <input
          type="text"
          v-model="form.search"
          @input="onFilter"
          placeholder="Buscar por N° Devolución, Factura, Orden o Cliente..."
          class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
        />
        <div class="absolute left-3 top-2.5 text-gray-400 text-sm">
          <i class="fa fa-search"></i>
        </div>
      </div>

      <div class="w-full md:w-auto flex items-center space-x-3">
        <select
          v-model="form.status"
          @change="onFilter"
          class="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Todos los Estados</option>
          <option value="approved">Aprobadas</option>
          <option value="pending">Pendientes</option>
          <option value="rejected">Rechazadas</option>
        </select>

        <button
          v-if="form.search || form.status"
          @click="resetFilter"
          class="px-3 py-2 text-xs text-gray-600 hover:text-gray-900 border rounded-md"
        >
          Limpiar
        </button>
      </div>
    </div>

    <!-- TABLA DE DEVOLUCIONES -->
    <div class="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap text-sm text-left">
          <thead class="bg-gray-50 text-gray-700 text-xs uppercase font-bold border-b border-gray-200">
            <tr>
              <th class="py-3 px-6">N° Devolución</th>
              <th class="py-3 px-6">Factura / Orden</th>
              <th class="py-3 px-6">Cliente</th>
              <th class="py-3 px-6">Fecha</th>
              <th class="py-3 px-6">Resolución</th>
              <th class="py-3 px-6 text-right">Monto Devuelto</th>
              <th class="py-3 px-6 text-center">Estado</th>
              <th class="py-3 px-6 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="devolution in devolutions.data"
              :key="devolution.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="py-4 px-6 font-mono font-bold text-blue-600">
                <Link :href="`/devolutions/${devolution.id}`">
                  #DEV-{{ padZero(devolution.id) }}
                </Link>
              </td>
              <td class="py-4 px-6">
                <Link :href="`/orders/${devolution.order_id}`" class="text-gray-800 hover:underline font-semibold flex items-center space-x-1">
                  <span>#{{ devolution.order ? (devolution.order.invoice_number || devolution.order.number_order) : '-' }}</span>
                  <i class="fa fa-external-link-alt text-[10px] text-gray-400"></i>
                </Link>
              </td>
              <td class="py-4 px-6 text-gray-700">
                <div class="font-medium">{{ devolution.order && devolution.order.client ? devolution.order.client.name : 'N/A' }}</div>
                <div class="text-xs text-gray-400">{{ devolution.order && devolution.order.client ? devolution.order.client.identification : '' }}</div>
              </td>
              <td class="py-4 px-6 text-gray-500 text-xs">
                {{ formatDateTime(devolution.created_at) }}
              </td>
              <td class="py-4 px-6">
                <span :class="getResolutionBadgeClass(devolution.resolution_type)" class="px-2.5 py-1 rounded text-xs font-bold uppercase">
                  {{ getResolutionText(devolution.resolution_type) }}
                </span>
              </td>
              <td class="py-4 px-6 text-right font-mono font-bold text-gray-900">
                ${{ formatCurrency(devolution.total_amount) }}
              </td>
              <td class="py-4 px-6 text-center">
                <span :class="getStatusBadgeClass(devolution.status)" class="px-2.5 py-1 rounded-full text-xs font-bold uppercase">
                  {{ getStatusText(devolution.status) }}
                </span>
              </td>
              <td class="py-4 px-6 text-center">
                <div class="flex items-center justify-center space-x-2">
                  <Link
                    :href="`/devolutions/${devolution.id}`"
                    class="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                    title="Ver Detalles"
                  >
                    <i class="fa fa-eye"></i>
                  </Link>

                  <a
                    :href="`/devolutions/${devolution.id}/pdf`"
                    target="_blank"
                    class="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded"
                    title="Imprimir PDF"
                  >
                    <i class="fa fa-print"></i>
                  </a>

                  <button
                    v-if="devolution.status === 'pending'"
                    @click="approveDevolution(devolution.id)"
                    class="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                    title="Aprobar Devolución"
                  >
                    <i class="fa fa-check"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="!devolutions.data.length">
              <td class="px-6 py-12 text-center text-gray-400" colspan="8">
                <i class="fa fa-rotate-left text-3xl mb-2 block text-gray-300"></i>
                No se encontraron devoluciones registradas.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <pagination class="mt-6" :links="devolutions.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'

export default {
  components: {
    Head,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    devolutions: Object,
    filters: Object,
  },
  data() {
    return {
      form: {
        search: this.filters ? this.filters.search : '',
        status: this.filters ? this.filters.status : '',
      }
    }
  },
  methods: {
    padZero(num) {
      return String(num).padStart(5, '0')
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString('es-VE', { hour12: false })
    },
    formatCurrency(amount) {
      const num = Number(amount) || 0
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    getStatusBadgeClass(status) {
      return {
        'approved': 'bg-green-100 text-green-800',
        'pending': 'bg-yellow-100 text-yellow-800',
        'rejected': 'bg-red-100 text-red-800'
      }[status] || 'bg-gray-100 text-gray-800'
    },
    getStatusText(status) {
      return {
        'approved': 'Aprobada',
        'pending': 'Pendiente',
        'rejected': 'Rechazada'
      }[status] || status
    },
    getResolutionBadgeClass(type) {
      return {
        'refund': 'bg-green-100 text-green-800',
        'exchange': 'bg-blue-100 text-blue-800',
        'store_credit': 'bg-purple-100 text-purple-800'
      }[type] || 'bg-gray-100 text-gray-800'
    },
    getResolutionText(type) {
      return {
        'refund': 'Dinero',
        'exchange': 'Cambio',
        'store_credit': 'Fondo'
      }[type] || type
    },
    onFilter: debounce(function () {
      this.$inertia.get('/devolutions', pickBy(this.form), {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350),
    resetFilter() {
      this.form.search = ''
      this.form.status = ''
      this.onFilter()
    },
    approveDevolution(id) {
      if (confirm('¿Está seguro de aprobar esta devolución? Esta acción actualizará inventarios y balances.')) {
        this.$inertia.post(`/devolutions/${id}/approve`)
      }
    }
  }
}
</script>
